import React, { useContext, useEffect, useState } from 'react';
import { DContext } from '../../../context/DataContext';
import Loading from '../../blocks/Loading';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function PackageDetails() {
  const { t, i18n } = useTranslation();
  const { BeURL, setLoader } = useContext(DContext);
  const { id } = useParams();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (BeURL && id) {
      setLoading(true);
      setNotFound(false);
      setLoader(true);
      fetch(`${BeURL}/fetch-package/${id}`, { credentials: 'include' })
        .then((res) => res.json())
        .then((data) => {
          setLoader(false);
          setLoading(false);
          if (data.success) {
            setPackageData(data.package);
            setNotFound(false);
          } else {
            setPackageData(null);
            setNotFound(true);
            alert(t('adminCommon.requestFailed'));
          }
        })
        .catch((err) => {
          setLoader(false);
          setLoading(false);
          console.error('Error fetching package:', err);
          setNotFound(true);
          alert(t('packageDetails.fetchError'));
        });
    }
  }, [BeURL, id, setLoader, t]);

  if (loading) {
    return <Loading />;
  }

  if (notFound || !packageData) {
    return (
      <div className="mx-auto my-6 max-w-2xl rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-[0_16px_38px_rgba(15,23,42,0.08)]">
        <h3 className="text-xl font-bold text-slate-900">{t('packageDetails.heading')}</h3>
        <p className="mt-2 text-sm text-slate-600">{t('packageDetails.notFound')}</p>
      </div>
    );
  }

  // Get the current language (e.g. 'en', 'fr', or 'ts')
  const currentLang = ['en', 'fr', 'ts'].includes(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en';

  return (
    <div className="mx-auto my-2 max-w-6xl space-y-4">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_16px_38px_rgba(15,23,42,0.08)]">
        <div className="bg-[#1739bd] px-5 py-4 text-white">
          <h3 className="text-xl font-bold tracking-tight">{packageData.title?.[currentLang] || packageData.title?.en || '-'}</h3>
        </div>
        <div className="space-y-4 p-4 sm:p-5">
          <p className="text-sm leading-relaxed text-slate-700">{packageData.description?.[currentLang] || packageData.description?.en || '-'}</p>
          <h5 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-500">{t('packageDetails.price')}</h5>

          <dl>
            <dt className="mb-2 text-sm font-bold text-slate-700">{t("packageDetails.packageCategory")}:</dt>
            <dd className='overflow-x-auto rounded-2xl border border-slate-200'>
              <table className='min-w-full divide-y divide-slate-200'>
                <thead className="bg-slate-100/80">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t("packageDetails.category")}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t("packageDetails.adult")}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t("packageDetails.kid")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {
                    packageData.priceType === "pkg" ?
                      <>
                        <tr>
                          <td className="px-4 py-3 text-slate-700">{t("packageDetails.fullDay")}</td>
                          <td className="px-4 py-3 text-slate-700">{`USD: $ ${packageData.price.usd.adult} / THB: ฿ ${packageData.price.thb.adult}` || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{`USD: $ ${packageData.price.usd.kid} / THB: ฿ ${packageData.price.thb.kid}` || '-'}</td>
                        </tr>
                        {packageData.isHalfPrice &&
                          <tr>
                            <td className="px-4 py-3 text-slate-700">{t("packageDetails.halfDay")}</td>
                            <td className="px-4 py-3 text-slate-700">{`USD: $ ${packageData.halfPrice.usd.adult} / THB: ฿ ${packageData.halfPrice.thb.adult}` || '-'}</td>
                            <td className="px-4 py-3 text-slate-700">{`USD: $ ${packageData.halfPrice.usd.kid} / THB: ฿ ${packageData.halfPrice.thb.kid}` || '-'}</td>
                          </tr>}
                      </>
                      :
                      <>
                        <tr>
                          <td className="px-4 py-3 text-slate-700">{t("packageDetails.speedBoat")}</td>
                          <td className="px-4 py-3 text-slate-700">{`USD: $ ${packageData.speedBoatPrice.usd.adult} / THB: ฿ ${packageData.speedBoatPrice.thb.adult}` || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{`USD: $ ${packageData.speedBoatPrice.usd.kid} / THB: ฿ ${packageData.speedBoatPrice.thb.kid}` || '-'}</td>
                        </tr>
                        {packageData.isLongTailPrice &&
                          <tr>
                            <td className="px-4 py-3 text-slate-700">{t("packageDetails.longtailBoat")}</td>
                            <td className="px-4 py-3 text-slate-700">{`USD: $ ${packageData.longTailPrice.usd.adult} / THB: ฿ ${packageData.longTailPrice.thb.adult}` || '-'}</td>
                            <td className="px-4 py-3 text-slate-700">{`USD: $ ${packageData.longTailPrice.usd.kid} / THB: ฿ ${packageData.longTailPrice.thb.kid}` || '-'}</td>
                          </tr>}
                      </>
                  }

                  {
                    packageData.isEntranceFees &&
                    <tr>
                      <td className="px-4 py-3 text-slate-700">{t("packageDetails.entranceFees")}</td>
                      <td className='px-4 py-3 text-center text-slate-700' colSpan={2}>{`USD: $ ${packageData.entranceFees.usd || 0} / THB: ฿ ${packageData.entranceFees.thb || 0}` || '-'}</td>
                    </tr>
                  }

                </tbody>
              </table>
            </dd>
          </dl>

          {/* Display Images */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {packageData.images.map((img, index) => (
              <div key={index}>
                {(() => {
                  const imageSrc = String(img || '').startsWith('http://') || String(img || '').startsWith('https://')
                    ? img
                    : `${BeURL}/${img}`;
                  return (
                    <img
                      src={imageSrc}
                      alt={`Package ${index}`}
                      className="h-48 w-full rounded-2xl border border-slate-200 object-cover"
                      style={{ height: '250px', objectFit: 'cover' }}
                    />
                  );
                })()}
              </div>
            ))}
          </div>

          {/* Activities */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <h5 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-500">{t('packageDetails.activities')}</h5>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">
              {packageData.activities[currentLang].map((activity, index) => (
                <li key={index}>{activity}</li>
              ))}
            </ul>
          </div>

          {/* Inclusion */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <h5 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-500">{t('packageDetails.inclusion')}</h5>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">
              {packageData.inclusion[currentLang].map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {/* What To Bring */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <h5 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-500">{t('packageDetails.whatToBring')}</h5>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">
              {packageData.whatToBring[currentLang].map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Destinations */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <h5 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-500">{t('packageDetails.destinations')}</h5>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">
              {packageData.destinations[currentLang].map((dest, index) => (
                <li key={index}>{dest}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className='text-center'>
        <button className='inline-flex items-center rounded-2xl border border-[#1739bd] px-4 py-2 text-sm font-semibold text-[#1739bd] transition hover:bg-indigo-50' onClick={() => window.location.href = `/admin/update-package/${id}`}>{t('packageDetails.updatePackage')}</button>
      </div>
    </div>
  );
}

export default PackageDetails;
